import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { nexusApi } from '../api/nexus';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import SOADrillInStudio from '../components/SOADrillInStudio';
import ActivePacksPanel from '../components/studios/ActivePacksPanel';
import ComplianceGuardrails from '../components/studios/ComplianceGuardrails';

const ContentStudio = () => {
  const navigate = useNavigate();
  const { isManagement, isAdmin } = useAuth();
  const [step, setStep] = useState(0); // Changed to 0 for SOA Drill
  const [loading, setLoading] = useState(false);
  const [contextData, setContextData] = useState({
    objective: 'Sales',
    platform: 'TikTok',
    duration: '60s',
    input: ''
  });
  const [ctxId, setCtxId] = useState(null);
  const [complianceResult, setComplianceResult] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [soaResults, setSOAResults] = useState(null); // V6.3 Phase 1A
  const [showActivePacksPanel, setShowActivePacksPanel] = useState(true); // Phase B4

  const handleCreateContext = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.contentContext(contextData);
      setCtxId(response.data.ctx_id);
      setStep(2);
    } catch (error) {
      console.error('Failed to create context:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrecheck = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.contentPrecheck({
        ctx_id: ctxId,
        input: contextData.input
      });
      setComplianceResult(response.data);
      
      if (response.data.precheck === 'ready_for_approval') {
        // Generate mock A/B/C outputs
        setOutputs([
          {
            id: 'A',
            title: 'Variant A: Emotional Connection',
            content: `Hook: ${contextData.input}\n\nCerita yang menyentuh hati tentang perjalanan kesihatan...\n\nCTA: Dapatkan sekarang!`,
            score: 92
          },
          {
            id: 'B',
            title: 'Variant B: Persuasive CTA',
            content: `${contextData.input}\n\nManfaat utama produk dengan urgency...\n\nPromo terhad masa!`,
            score: 88
          },
          {
            id: 'C',
            title: 'Variant C: Educational',
            content: `Tahukah anda? ${contextData.input}\n\nMaklumat penting untuk kesihatan...\n\nKetahui lebih lanjut.`,
            score: 85
          }
        ]);
        setStep(3);
      }
    } catch (error) {
      console.error('Precheck failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium">Objective</label>
        <select
          className="w-full p-3 border rounded-lg"
          value={contextData.objective}
          onChange={(e) => setContextData({ ...contextData, objective: e.target.value })}
          data-testid="objective-select"
        >
          <option value="Sales">Sales</option>
          <option value="Awareness">Awareness</option>
          <option value="Engagement">Engagement</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Platform</label>
        <select
          className="w-full p-3 border rounded-lg"
          value={contextData.platform}
          onChange={(e) => setContextData({ ...contextData, platform: e.target.value })}
          data-testid="platform-select"
        >
          <option value="TikTok">TikTok</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Duration</label>
        <Input
          placeholder="e.g., 60s, 1m 30s"
          value={contextData.duration}
          onChange={(e) => setContextData({ ...contextData, duration: e.target.value })}
          data-testid="duration-input"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Content Idea</label>
        <Textarea
          placeholder="Describe your content idea..."
          value={contextData.input}
          onChange={(e) => setContextData({ ...contextData, input: e.target.value })}
          rows={4}
          data-testid="content-input"
        />
        
        {/* Phase B4: Real-time Compliance Checking */}
        {contextData.input && (
          <ComplianceGuardrails
            text={contextData.input}
            studioType="content"
            productIds={[]}
            campaignId={null}
            onComplianceChange={(result) => setComplianceResult(result)}
          />
        )}
      </div>

      <Button
        onClick={handleCreateContext}
        disabled={loading || !contextData.input}
        className="w-full bg-emzi-blue hover:bg-blue-600 text-white font-semibold h-12"
        data-testid="create-context-button"
      >
        {loading ? 'Processing...' : 'Generate Content →'}
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription>
          🔍 Running compliance precheck...
        </AlertDescription>
      </Alert>

      <Button
        onClick={handlePrecheck}
        disabled={loading}
        className="w-full"
        data-testid="precheck-button"
      >
        {loading ? 'Checking...' : 'Run Compliance Check'}
      </Button>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      {complianceResult && (
        <Alert 
          className={complianceResult.precheck === 'ready_for_approval' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}
          data-testid="compliance-result"
        >
          <AlertDescription>
            {complianceResult.precheck === 'ready_for_approval' ? (
              <>
                ✓ Compliance Check Passed<br />
                Risk Score: {complianceResult.compliance_risk}/15
              </>
            ) : (
              <>
                ✗ Compliance Failed<br />
                Risk Score: {complianceResult.compliance_risk}/15
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <h3 className="font-semibold">Generated Variants (A/B/C)</h3>
        {outputs.map((output) => (
          <Card key={output.id} data-testid={`output-${output.id}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{output.title}</CardTitle>
                <Badge variant="default">Score: {output.score}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap text-gray-700">{output.content}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => { setStep(1); setOutputs([]); setComplianceResult(null); }}
          className="flex-1"
          data-testid="new-content-button"
        >
          Create New
        </Button>
        <Button
          onClick={() => navigate('/dashboard')}
          className="flex-1"
          data-testid="back-dashboard-button"
        >
          Back to Dashboard
        </Button>
      </div>
    </div>
  );

  return (
    <div className="nexus-content overflow-hidden bg-white">
      {/* EMZI Header - Normalized with Dashboard */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
              <span className="text-white font-black text-2xl">🎬</span>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-wide text-white">Content Studio</h1>
              <p className="text-sm text-white font-medium">Create engaging content with A/B/C variants</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {(isManagement() || isAdmin()) && (
              <span className="text-white text-xs font-medium opacity-90" title="System certified under EMZI ISO-Aligned Compliance Protocol">
                🟢 ISO Compliance Active
              </span>
            )}
            <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105" data-testid="back-button">
              ← Back
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-0" style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
        {/* Active Packs Panel - Phase B4 */}
        {showActivePacksPanel && (
          <div style={{ 
            width: '320px', 
            flexShrink: 0, 
            height: 'calc(100vh - 140px)', 
            position: 'sticky', 
            top: '12px',
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <ActivePacksPanel
              studioType="content"
              productIds={[]}
              campaignId={null}
            />
          </div>
        )}
        
        {/* Main Content Card */}
        <div style={{ flex: 1, minWidth: 0 }}>
        <Card className="shadow-xl border-0" style={{boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)'}} data-testid="content-studio-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emzi-blue bg-opacity-10 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-emzi-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
              </div>
              <div>
                <CardTitle className="text-gray-900 font-bold">Content Studio</CardTitle>
                <CardDescription className="text-sm text-gray-600">Create engaging content with A/B/C variants</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-emzi-blue' : 'bg-gray-200'
                  }`}
                  data-testid={`progress-step-${s}`}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {step === 0 && (
              <div className="fixed inset-0 z-50 bg-white">
                <SOADrillInStudio
                  onComplete={(results) => {
                    setSOAResults(results);
                    setStep(1);
                  }}
                  onSkip={() => setStep(1)}
                  studioType="content"
                />
              </div>
            )}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default ContentStudio;
